/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getGlobalConfigFromCache } from '@/features/globalConfig';
import { prefetchCMSStoreContent } from '@/features/locations/api/getContentStackStore';
import {
  getStoreDetailsFromCache,
  prefetchStoreDetails,
} from '@/features/locations/api/getStoreDetails';
import { StoreDetailsPage } from '@/features/locations/pages/StoreDetailsPage/StoreDetailsPage';
import { getLocationsParams } from '@/features/locations/utils/getLocationsParams';
import { getLocationsRedirectUrl } from '@/features/locations/utils/getLocationsRedirectUrl';
import { isValidLocationsLocale } from '@/features/locations/utils/isValidLocationsLocale';
import { type PageContextType } from '@/next-types';
import { configureSharedPageData } from '@/utils/configureSharedPageData.server';
import { dehydrate } from '@tanstack/react-query';
import { prefetchLocationsSEOMetadata } from '@/features/locations/api/getLocationsSEOMetadata';
import { isServer } from '@/utils/isServer';

export const config = {
  unstable_JsPreload: false,
};

function StoreDetailsPageComponent() {
  return <StoreDetailsPage />;
}

StoreDetailsPageComponent.getInitialProps = async (ctx: PageContextType) => {
  if (isServer && ctx.res) {
    const { queryClient, initialAppState, axios } = await configureSharedPageData(ctx);
    const { locale } = initialAppState;

    if (!isValidLocationsLocale(ctx.asPath, locale)) {
      ctx.res.writeHead(302, { Location: '/' }).end();
      return {};
    }

    const {
      locationsHome,
      state = '',
      city = '',
      address = '',
    } = getLocationsParams(initialAppState.locale, ctx.query);
    const globalConfig = getGlobalConfigFromCache(queryClient);

    const yextURL = globalConfig?.YEXT_URL ?? '';
    const yextAPIKey = globalConfig?.YEXT_API_KEY ?? '';
    const yextVersion = globalConfig?.YEXT_VERSION ?? '';

    const options = {
      locale,
      state,
      city,
      address,
      yextURL,
      yextAPIKey,
      yextVersion,
    };

    await prefetchStoreDetails(queryClient, axios, options);
    const storeDetails = getStoreDetailsFromCache(queryClient, options);

    if (!storeDetails?.meta.id) {
      ctx.res
        .writeHead(302, { Location: getLocationsRedirectUrl(locationsHome, state, city) })
        .end();
      return {};
    }

    await Promise.all([
      prefetchCMSStoreContent({ pageType: 'STORE_DETAILS', state, city }, queryClient, ctx),
      prefetchLocationsSEOMetadata(
        {
          pageType: 'STORE_DETAILS',
          state,
          city,
        },
        queryClient,
        ctx,
      ),
    ]);

    return {
      initialAppState,
      dehydratedState: dehydrate(queryClient),
    };
  }

  return {};
};

export default StoreDetailsPageComponent;
